import React, { useMemo, useState } from 'react';
import { Grid, TableBody } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Table, AlertColor } from '@mui/material';
import { useMeContext } from 'modules/me';
import { formatDateFromIsoString } from 'utils/date-time';
import {
  Collector,
  CollectorSetting,
  CollectorSettingScope,
  CollectorSettingsResult,
  InstalledCollectorSettingsQuery,
  Setting
} from 'graphql/types';
import { CopyrightFooter } from 'modules/common/components/CopyrightFooter';
import { ToastSnackBar } from '../../Settings/ToastSnackBar';
import { InstalledCollectorHeader } from './InstalledCollectorHeader';
import { InstalledCollectorRow } from './InstalledCollectorRow';
import { InstalledCollectorsPageSkeleton } from './InstalledCollectorsPageSkeleton';
import { ConfirmDialog } from './Cells/ConfirmDialog';
import { CollectorsLoadMore } from './CollectorsLoadMore';

const useStyles = makeStyles((theme) => ({
  container: {
    background: theme.palette.information.pageBackground,
    height: '100%',
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  grid: {
    opacity: '1',
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    background: theme.palette.information.pageBackground
  },
  table: {
    width: 'calc(100% - 36px)',
    borderBottom: 'none'
  }
}));

type InstalledCollectorsListPageProps = {
  isAdmin: boolean;
  data: InstalledCollectorSettingsQuery | undefined;
  loading: boolean;
  loadMore: (cursor: string) => void;
};
type UserPreferencesInfo = {
  installId: string;
  name: string;
  snackBarMessage: string | undefined;
};
type onOptOut = () => void;
export const InstalledCollectorsListPage: React.FC<InstalledCollectorsListPageProps> = ({
  isAdmin,
  data,
  loading,
  loadMore
}: InstalledCollectorsListPageProps) => {
  const classes = useStyles();

  const me = useMeContext();
  const category = 'success' as AlertColor;
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [
    userPreferencesInfo,
    setUserPreferencesInfo
  ] = useState<UserPreferencesInfo>({
    installId: '',
    name: '',
    snackBarMessage: ''
  });
  const [onOptOut, setOnOptOut] = useState<onOptOut>(() => {});
  const [openSnackBar, setOpenSnackBar] = useState(false);

  const installedCollectorRows = useMemo(() => {
    const settings: CollectorSetting[] = (data?.collectorSettings?.items ??
      []) as CollectorSetting[];
    return settings.filter(Boolean).map((item: CollectorSetting) => {
      const { collectorMetadata, scope, settings, installId } = item as {
        collectorMetadata?: Collector;
        scope: CollectorSettingScope;
        settings: Setting[];
        installId: string;
      };
      if (collectorMetadata !== null) {
        const companyCollectorUserPreferences = item!.userPreferences.filter(
          (setting) => setting?.key === 'companyCollectorOptOutEnabled'
        );
        const optInStatus =
          companyCollectorUserPreferences.length === 0 ||
          companyCollectorUserPreferences.some(
            (setting) =>
              setting?.key === 'companyCollectorOptOutEnabled' &&
              setting?.value === 'true'
          );
        const date = formatDateFromIsoString(item?.installedDate, me);
        return (
          <InstalledCollectorRow
            key={installId}
            info={collectorMetadata}
            scope={scope}
            installedDate={date}
            settings={settings}
            isAdmin={isAdmin}
            installId={installId}
            optInStatus={optInStatus}
            setOpenConfirmDialog={setOpenConfirmDialog}
            setUserPreferencesInfo={setUserPreferencesInfo}
            setOnOptOut={setOnOptOut}
            setOpenSnackBar={setOpenSnackBar}
            allowUserToOptOut={item?.canOptOut ?? true}
          />
        );
      }
    });
  }, [data, isAdmin, me]);

  if (!data) return <InstalledCollectorsPageSkeleton />;
  return (
    <div className={classes.container}>
      <ConfirmDialog
        open={openConfirmDialog}
        setOpenConfirmDialog={setOpenConfirmDialog}
        onOptOut={onOptOut}
        userPreferencesInfo={userPreferencesInfo}
      />
      <Grid
        container
        className={classes.grid}
        spacing={1}
        data-testid="installed-collectors-container"
      >
        <Table
          className={classes.table}
          size="small"
          data-testid="installed-collectors"
          stickyHeader
        >
          <InstalledCollectorHeader />
          <TableBody>{installedCollectorRows}</TableBody>
          <CollectorsLoadMore
            loading={loading}
            collectorSettingsResult={
              data?.collectorSettings as CollectorSettingsResult
            }
            loadMore={loadMore}
          />
        </Table>
      </Grid>
      <CopyrightFooter />
      <ToastSnackBar
        show={openSnackBar}
        category={category}
        message={`${userPreferencesInfo?.snackBarMessage} ${userPreferencesInfo?.name}`}
      />
    </div>
  );
};
export default React.memo(InstalledCollectorsListPage);
